import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Play, Pause, CheckCircle, XCircle, RotateCcw, UserPlus, ChevronDown, ChevronUp, Instagram, ExternalLink, MapPin, DollarSign, Brain, Loader2, TrendingUp } from "lucide-react";
import { useRef } from "react";

interface GearInventory {
  gear_owned?: string[];
  gear_notes?: string;
  needs_amp?: boolean;
  needs_mic?: boolean;
  needs_monitor?: boolean;
  needs_music_stand?: boolean;
  needs_power?: boolean;
  tech_rider_notes?: string;
}

interface Audition {
  id: string;
  name: string;
  email: string;
  phone: string;
  instagram_handle: string | null;
  music_profile_url: string | null;
  primary_instrument: string;
  secondary_instruments: string[];
  region: string;
  mailing_address: string | null;
  desired_hourly_rate: number | null;
  availability_weekdays: boolean;
  availability_weekends: boolean;
  travel_radius_miles: number;
  overnight_travel: boolean;
  minimum_notice: string;
  owns_gear: boolean;
  cancellation_last_12mo: boolean;
  genre_comfort: Record<string, string>;
  scenario_response: string | null;
  reliability_score: number | null;
  talent_score: number | null;
  luxury_score: number | null;
  marketability_score: number | null;
  tier: string | null;
  status: string;
  admin_notes: string | null;
  submitted_at: string;
  reviewed_at: string | null;
  gear_inventory: GearInventory | null;
}

interface Recording {
  id: string;
  audition_id: string;
  test_key: string;
  storage_path: string;
  duration_seconds: number;
}

const TEST_LABELS: Record<string, string> = {
  lock_in: "Test A: Copy-Cat — Just the Two of Us",
  improvisation: "Test B: Play Along — Autumn Leaves",
  follow_direction: "Test C: Play Along — Amazing Grace",
};

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  submitted: { label: "Submitted", className: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  reviewed: { label: "Reviewed", className: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
  approved: { label: "Approved", className: "bg-green-500/20 text-green-400 border-green-500/30" },
  rejected: { label: "Rejected", className: "bg-red-500/20 text-red-400 border-red-500/30" },
};

export const AuditionReviewPanel = () => {
  const { toast } = useToast();
  const [auditions, setAuditions] = useState<Audition[]>([]);
  const [recordings, setRecordings] = useState<Record<string, Recording[]>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [editingNotes, setEditingNotes] = useState<Record<string, string>>({});
  const [scoringId, setScoringId] = useState<string | null>(null);

  // Audio playback
  const [playingPath, setPlayingPath] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    fetchAuditions();
  }, [statusFilter]);

  const fetchAuditions = async () => {
    setIsLoading(true);
    let query = supabase
      .from("musician_auditions")
      .select("*")
      .order("submitted_at", { ascending: false });

    if (statusFilter !== "all") {
      query = query.eq("status", statusFilter);
    }

    const { data, error } = await query;
    if (error) {
      console.error("Failed to fetch auditions:", error);
    } else {
      setAuditions((data as any[]) || []);
    }
    setIsLoading(false);
  };

  const fetchRecordings = async (auditionId: string) => {
    if (recordings[auditionId]) return;
    
    const { data, error } = await supabase
      .from("audition_recordings")
      .select("*")
      .eq("audition_id", auditionId);

    if (!error && data) {
      setRecordings(prev => ({ ...prev, [auditionId]: data as any[] }));
    }
  };

  const toggleExpand = (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      fetchRecordings(id);
    }
  };

  const playAudio = async (storagePath: string) => {
    if (playingPath === storagePath) {
      audioRef.current?.pause();
      setPlayingPath(null);
      return;
    }

    const { data } = await supabase.storage
      .from("audition-recordings")
      .createSignedUrl(storagePath, 300);

    if (data?.signedUrl) {
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(data.signedUrl);
      audio.onended = () => setPlayingPath(null);
      audio.play();
      audioRef.current = audio;
      setPlayingPath(storagePath);
    }
  };

  const updateStatus = async (id: string, newStatus: string) => {
    const { error } = await supabase
      .from("musician_auditions")
      .update({ 
        status: newStatus, 
        reviewed_at: new Date().toISOString(),
      })
      .eq("id", id);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Updated", description: `Status set to ${newStatus}` });
      fetchAuditions();

      // Send email notification for approve/reject
      if (newStatus === "approved" || newStatus === "rejected") {
        try {
          const session = await supabase.auth.getSession();
          await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-audition-email`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
                "Authorization": `Bearer ${session.data.session?.access_token}`,
              },
              body: JSON.stringify({ type: newStatus, audition_id: id }),
            }
          );
          toast({ title: "Email sent", description: `${newStatus === "approved" ? "Approval" : "Rejection"} email sent to musician` });
        } catch (e) {
          console.error("Email notification failed:", e);
        }
      }
    }
  };

  const updateTier = async (id: string, tier: string) => {
    const { error } = await supabase
      .from("musician_auditions")
      .update({ tier })
      .eq("id", id);

    if (!error) {
      toast({ title: "Tier Updated", description: `Set to Tier ${tier}` });
      fetchAuditions();
    }
  };

  const saveNotes = async (id: string) => {
    const notes = editingNotes[id];
    if (notes === undefined) return;

    const { error } = await supabase
      .from("musician_auditions")
      .update({ admin_notes: notes })
      .eq("id", id);

    if (!error) {
      toast({ title: "Notes saved" });
      fetchAuditions();
    }
  };

  const addToRoster = async (audition: Audition) => {
    const r = audition.reliability_score ?? 0;
    const t = audition.talent_score ?? 0;
    const l = audition.luxury_score ?? 0;
    const m = audition.marketability_score ?? 0;
    const compositeScore = Math.round(r * 0.25 + t * 0.35 + l * 0.25 + m * 0.15);

    const { data: musician, error } = await supabase.from("musicians").insert({
      // Contact
      name: audition.name,
      email: audition.email,
      phone: audition.phone,
      // Instruments
      primary_instrument: audition.primary_instrument,
      instruments: [audition.primary_instrument, ...audition.secondary_instruments],
      // Logistics
      region: audition.region,
      mailing_address: audition.mailing_address ?? null,
      availability_weekdays: audition.availability_weekdays ?? false,
      availability_weekends: audition.availability_weekends ?? false,
      travel_radius_miles: audition.travel_radius_miles ?? null,
      minimum_notice: audition.minimum_notice ?? null,
      // Scoring
      reliability_score: audition.reliability_score ?? null,
      talent_score: audition.talent_score ?? null,
      luxury_score: audition.luxury_score ?? null,
      marketability_score: audition.marketability_score ?? null,
      composite_score: compositeScore,
      tier: audition.tier ?? null,
      // Profile
      instagram_handle: audition.instagram_handle ?? null,
      music_profile_url: audition.music_profile_url ?? null,
      // Gear
      owns_gear: audition.owns_gear ?? false,
      gear_inventory: audition.gear_inventory ?? {},
      genre_comfort: audition.genre_comfort ?? {},
      // Rate & link
      hourly_rate: audition.desired_hourly_rate ?? null,
      audition_id: audition.id,
      is_active: true,
      notes: `Tier ${audition.tier || "?"} | Composite: ${compositeScore}/100 | R:${r} T:${t} L:${l} M:${m}`,
    } as any).select("id").single();

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      if (musician?.id) {
        await supabase.from("musician_auditions").update({ musician_id: musician.id } as any).eq("id", audition.id);
      }
      toast({ title: "Added to Roster", description: `${audition.name} added as Tier ${audition.tier || "?"} (Score: ${compositeScore}/100)` });
      fetchAuditions();
    }
  };

  const scoreWithAI = async (auditionId: string) => {
    setScoringId(auditionId);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/score-audition`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ audition_id: auditionId }),
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(err.error || `Error ${response.status}`);
      }

      const result = await response.json();
      toast({
        title: `AI Scored: Tier ${result.tier}`,
        description: `Talent: ${result.talent_score} | Luxury: ${result.luxury_score} | Marketability: ${result.marketability_score} | Composite: ${result.composite_score}${result.disqualified ? " ⚠️ DISQUALIFIED" : ""}`,
      });
      fetchAuditions();
    } catch (err: any) {
      console.error("AI scoring error:", err);
      toast({ title: "Scoring failed", description: err.message, variant: "destructive" });
    } finally {
      setScoringId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Audition Submissions</CardTitle>
            <CardDescription>Review musician vetting recordings and scores</CardDescription>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-center text-muted-foreground py-8">Loading...</p>
        ) : auditions.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No audition submissions yet.</p>
        ) : (
          <div className="space-y-2">
            {auditions.map(a => (
              <div key={a.id} className="border rounded-lg overflow-hidden">
                {/* Row */}
                <div
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => toggleExpand(a.id)}
                >
                  <div className="flex items-center gap-3">
                    {expandedId === a.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    <div>
                      <p className="font-medium text-sm">{a.name}</p>
                      <p className="text-xs text-muted-foreground">{a.primary_instrument} • {a.region}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {a.tier && (
                      <Badge variant="outline" className="text-xs">Tier {a.tier}</Badge>
                    )}
                    {a.reliability_score !== null && (
                      <Badge variant="secondary" className="text-xs">
                        Reliability: {a.reliability_score}
                      </Badge>
                    )}
                    <Badge className={STATUS_CONFIG[a.status]?.className || ""}>
                      {STATUS_CONFIG[a.status]?.label || a.status}
                    </Badge>
                    <p className="text-xs text-muted-foreground">
                      {new Date(a.submitted_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {/* Expanded Detail */}
                {expandedId === a.id && (
                  <div className="border-t p-4 space-y-4 bg-muted/10">
                    {/* Contact & Profile */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                      <div><span className="text-muted-foreground block text-xs">Email</span> {a.email}</div>
                      <div><span className="text-muted-foreground block text-xs">Phone</span> {a.phone}</div>
                      <div>
                        <span className="text-muted-foreground block text-xs">Instagram</span>
                        {a.instagram_handle ? (
                          <a href={`https://instagram.com/${a.instagram_handle.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
                            <Instagram className="h-3 w-3" /> {a.instagram_handle}
                          </a>
                        ) : <span className="text-muted-foreground">—</span>}
                      </div>
                      <div>
                        <span className="text-muted-foreground block text-xs">Music Profile</span>
                        {a.music_profile_url ? (
                          <a href={a.music_profile_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline truncate">
                            <ExternalLink className="h-3 w-3 shrink-0" /> {new URL(a.music_profile_url).hostname}
                          </a>
                        ) : <span className="text-muted-foreground">—</span>}
                      </div>
                    </div>

                    {/* Address, Rate, Instrument details */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-muted-foreground block text-xs">Mailing Address</span>
                        <span className="flex items-start gap-1"><MapPin className="h-3 w-3 mt-0.5 shrink-0 text-muted-foreground" /> {a.mailing_address || "—"}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block text-xs">Desired Rate</span>
                        <span className="flex items-center gap-1"><DollarSign className="h-3 w-3 text-muted-foreground" /> {a.desired_hourly_rate != null ? `$${a.desired_hourly_rate}/hr` : "—"}</span>
                      </div>
                      <div><span className="text-muted-foreground block text-xs">Primary Instrument</span> {a.primary_instrument}</div>
                      <div><span className="text-muted-foreground block text-xs">Secondary</span> {a.secondary_instruments?.join(", ") || "None"}</div>
                    </div>

                    {/* Availability */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                      <div className="p-2 rounded bg-card border">
                        <p className="text-muted-foreground">Weekdays</p>
                        <p className="font-medium">{a.availability_weekdays ? "✓ Yes" : "✗ No"}</p>
                      </div>
                      <div className="p-2 rounded bg-card border">
                        <p className="text-muted-foreground">Weekends</p>
                        <p className="font-medium">{a.availability_weekends ? "✓ Yes" : "✗ No"}</p>
                      </div>
                      <div className="p-2 rounded bg-card border">
                        <p className="text-muted-foreground">Travel</p>
                        <p className="font-medium">{a.travel_radius_miles} mi {a.overnight_travel ? "(overnight OK)" : ""}</p>
                      </div>
                      <div className="p-2 rounded bg-card border">
                        <p className="text-muted-foreground">Min Notice</p>
                        <p className="font-medium">{a.minimum_notice}</p>
                      </div>
                    </div>

                    {/* Gear & Tech Rider */}
                    {(() => {
                      const gear = (a as any).gear_inventory as GearInventory | null;
                      const gearOwned = gear?.gear_owned || [];
                      const techNeeds = [
                        gear?.needs_amp && "Amplifier",
                        gear?.needs_mic && "Microphone",
                        gear?.needs_monitor && "Stage Monitor",
                        gear?.needs_music_stand && "Music Stand",
                        gear?.needs_power && "Power / Extension",
                      ].filter(Boolean);
                      
                      return (
                        <div className="space-y-2">
                          <p className="text-sm font-medium">Gear & Tech Rider</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="p-3 rounded bg-card border">
                              <p className="text-xs text-muted-foreground font-medium mb-1">Owns Gear: {a.owns_gear ? "✓ Yes" : "✗ No"}</p>
                              {gearOwned.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {gearOwned.map((item: string) => (
                                    <Badge key={item} variant="secondary" className="text-[10px]">{item}</Badge>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground italic">No gear listed</p>
                              )}
                              {gear?.gear_notes && (
                                <p className="text-xs mt-2 text-muted-foreground italic">"{gear.gear_notes}"</p>
                              )}
                            </div>
                            <div className="p-3 rounded bg-card border">
                              <p className="text-xs text-muted-foreground font-medium mb-1">Needs From Venue/Us</p>
                              {techNeeds.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {techNeeds.map((item) => (
                                    <Badge key={item as string} variant="outline" className="text-[10px] border-orange-500/40 text-orange-500">{item}</Badge>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground italic">Self-sufficient</p>
                              )}
                              {gear?.tech_rider_notes && (
                                <p className="text-xs mt-2 text-muted-foreground italic">"{gear.tech_rider_notes}"</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Audio Recordings */}
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Recordings</p>
                      {recordings[a.id]?.length ? (
                        recordings[a.id].map(rec => (
                          <div key={rec.id} className="flex items-center justify-between p-2 rounded bg-card border">
                            <div>
                              <p className="text-sm font-medium">{TEST_LABELS[rec.test_key] || rec.test_key}</p>
                              <p className="text-xs text-muted-foreground">{Math.round(rec.duration_seconds)}s</p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); playAudio(rec.storage_path); }}
                            >
                              {playingPath === rec.storage_path ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                            </Button>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-muted-foreground">Loading recordings...</p>
                      )}
                    </div>

                    {/* Scores */}
                    <div className="grid grid-cols-4 gap-2">
                      <div className="p-3 rounded bg-card border text-center">
                        <p className="text-xs text-muted-foreground">Reliability</p>
                        <p className="text-xl font-bold text-primary">{a.reliability_score ?? "—"}</p>
                      </div>
                      <div className="p-3 rounded bg-card border text-center">
                        <p className="text-xs text-muted-foreground">Talent</p>
                        <p className="text-xl font-bold text-muted-foreground">{a.talent_score ?? "Pending"}</p>
                      </div>
                      <div className="p-3 rounded bg-card border text-center">
                        <p className="text-xs text-muted-foreground">Luxury</p>
                        <p className="text-xl font-bold text-muted-foreground">{a.luxury_score ?? "Pending"}</p>
                      </div>
                      <div className="p-3 rounded bg-card border text-center">
                        <p className="text-xs text-muted-foreground flex items-center justify-center gap-1"><TrendingUp className="h-3 w-3" /> Marketability</p>
                        <p className="text-xl font-bold text-muted-foreground">
                          {(a as any).marketability_score ?? (() => {
                            const notes = a.admin_notes || "";
                            const match = notes.match(/Marketability \((\d+)\/100\)/);
                            return match ? match[1] : "Pending";
                          })()}
                        </p>
                      </div>
                    </div>

                    {/* Genre Comfort */}
                    {a.genre_comfort && typeof a.genre_comfort === 'object' && Object.keys(a.genre_comfort).length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-2">Genre Comfort</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(a.genre_comfort).map(([key, val]) => (
                            <Badge key={key} variant="outline" className="text-xs">
                              {key.replace(/_/g, " ")}: {String(val).replace(/_/g, " ")}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Scenario Responses - from genre_data.scenario_responses */}
                    <div>
                      <p className="text-sm font-medium mb-2">Professionalism Scenarios</p>
                      <div className="space-y-2">
                        {a.scenario_response && (
                          <div className="text-sm bg-card p-3 rounded border">
                            <span className="font-medium text-xs text-muted-foreground block mb-1">Unknown Song Request</span>
                            <p className="italic">"{a.scenario_response}"</p>
                          </div>
                        )}
                        {(() => {
                          const gc = a.genre_comfort as any;
                          const scenarios = gc?.scenario_responses || {};
                          return (
                            <>
                              {scenarios.volume_complaint && (
                                <div className="text-sm bg-card p-3 rounded border">
                                  <span className="font-medium text-xs text-muted-foreground block mb-1">Volume Complaint</span>
                                  <p className="italic">"{scenarios.volume_complaint}"</p>
                                </div>
                              )}
                              {scenarios.gear_failure && (
                                <div className="text-sm bg-card p-3 rounded border">
                                  <span className="font-medium text-xs text-muted-foreground block mb-1">Gear Failure</span>
                                  <p className="italic">"{scenarios.gear_failure}"</p>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Admin Notes */}
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Admin Notes</p>
                      <Textarea
                        value={editingNotes[a.id] ?? a.admin_notes ?? ""}
                        onChange={(e) => setEditingNotes(prev => ({ ...prev, [a.id]: e.target.value }))}
                        placeholder="Add review notes..."
                        className="min-h-[80px]"
                      />
                      <Button size="sm" variant="outline" onClick={() => saveNotes(a.id)}>
                        Save Notes
                      </Button>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-2 border-t">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => scoreWithAI(a.id)}
                        className="gap-1"
                        disabled={scoringId === a.id}
                      >
                        {scoringId === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Brain className="h-3 w-3" />}
                        {scoringId === a.id ? "Scoring..." : "Score with AI"}
                      </Button>

                      <Select value={a.tier || ""} onValueChange={(val) => updateTier(a.id, val)}>
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="Set Tier" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="A">Tier A</SelectItem>
                          <SelectItem value="B">Tier B</SelectItem>
                          <SelectItem value="C">Tier C</SelectItem>
                        </SelectContent>
                      </Select>

                      <Button size="sm" onClick={() => updateStatus(a.id, "approved")} className="gap-1" disabled={a.status === "approved"}>
                        <CheckCircle className="h-3 w-3" /> Approve
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => updateStatus(a.id, "rejected")} className="gap-1" disabled={a.status === "rejected"}>
                        <XCircle className="h-3 w-3" /> Reject
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => updateStatus(a.id, "submitted")} className="gap-1">
                        <RotateCcw className="h-3 w-3" /> Re-audition
                      </Button>
                      {a.status === "approved" && (
                        <Button size="sm" variant="secondary" onClick={() => addToRoster(a)} className="gap-1">
                          <UserPlus className="h-3 w-3" /> Add to Roster
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
